import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  writeBatch
} from '@angular/fire/firestore';
import { SupportedLanguage } from './website.service';

export interface PostCategory {
  id: string;
  name: string;
  slug?: string;
  domain?: string;
  parentId?: string | null;
  translations?: Record<string, string>;
}

/**
 * Post – één Firestore-document per taal.
 * Taalversies worden gekoppeld via `basePostId`.
 */
export interface Post {
  id: string;
  title: string;
  content: string;
  language: SupportedLanguage;
  basePostId?: string | null;
  createdAt?: Date;
  updatedAt?: Date;
  status: 'draft' | 'published';
  categoryIds?: string[];
  domain?: string;
  domains?: string[];
  featuredImage?: string;
  slug?: string;
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  /** Optioneel ruw HTML/CSS/JS blok dat NA de post-content wordt gerenderd
   *  op de live site. Scripts binnenin worden na mount uitgevoerd. */
  htmlBlock?: string;
}

export type PostCreate = Omit<Post, 'id'> & { id?: never };
export type PostUpdate = Partial<Omit<Post, 'id'>>;

@Injectable({ providedIn: 'root' })
export class PostService {
  constructor(private firestore: Firestore) {}

  /**
   * Converteert een Firestore-document naar een Post.
   * Geen backward compatibility — alle velden zijn simpele strings.
   */
  private docToPost(d: { id: string; data: () => Record<string, unknown> }): Post {
    const data = d.data();
    const doms = this.domainsFrom(data);

    return {
      id: d.id,
      title: String(data['title'] ?? ''),
      content: String(data['content'] ?? ''),
      language: (data['language'] as SupportedLanguage) || 'nl',
      basePostId: (data['basePostId'] as string) || null,
      status: (data['status'] as 'draft' | 'published') || 'draft',
      categoryIds: this.categoryIdsFrom(data),
      domain: doms[0] ?? (data['domain'] as string),
      domains: doms,
      createdAt: (data['createdAt'] as { toDate?: () => Date })?.toDate?.(),
      updatedAt: (data['updatedAt'] as { toDate?: () => Date })?.toDate?.(),
      featuredImage: data['featuredImage'] as string | undefined,
      slug: data['slug'] as string | undefined,
      metaTitle: String(data['metaTitle'] ?? '') || undefined,
      metaDescription: String(data['metaDescription'] ?? '') || undefined,
      metaKeywords: Array.isArray(data['metaKeywords']) ? data['metaKeywords'].filter((x: unknown) => typeof x === 'string') : [],
      ogTitle: String(data['ogTitle'] ?? '') || undefined,
      ogDescription: String(data['ogDescription'] ?? '') || undefined,
      ogImage: data['ogImage'] as string | undefined,
      htmlBlock: data['htmlBlock'] as string | undefined
    } as Post;
  }

  private domainsFrom(data: Record<string, unknown>): string[] {
    if (Array.isArray(data['domains']) && data['domains'].length > 0) {
      return (data['domains'] as string[]).filter((x) => typeof x === 'string');
    }
    const d = data['domain'];
    if (d != null && String(d).trim() !== '') return [String(d).trim()];
    return [];
  }

  private categoryIdsFrom(data: Record<string, unknown>): string[] {
    if (Array.isArray(data['categoryIds']) && data['categoryIds'].length > 0) {
      return (data['categoryIds'] as string[]).filter((x) => typeof x === 'string');
    }
    return [];
  }

  /**
   * Haalt alle posts op voor een domein + taal.
   * Optioneel filteren op categorie.
   *
   * Strategie: query op `language` + `domain`/`domains`.
   * Client-side sortering en categorie-filtering om composite index problemen te voorkomen.
   */
  async getPosts(domain: string, language: SupportedLanguage, categoryFilter?: string | null): Promise<Post[]> {
    if (!domain) return [];

    const postsRef = collection(this.firestore, 'posts');

    // Twee queries: één op 'domain' veld, één op 'domains' array
    // Elk met language filter
    const [snapDomain, snapDomains] = await Promise.all([
      getDocs(query(postsRef, where('domain', '==', domain), where('language', '==', language))),
      getDocs(query(postsRef, where('domains', 'array-contains', domain), where('language', '==', language)))
    ]);

    // Merge en deduplicate
    const byId = new Map<string, Post>();
    for (const d of [...snapDomain.docs, ...snapDomains.docs]) {
      if (!byId.has(d.id)) {
        byId.set(d.id, this.docToPost(d as unknown as { id: string; data: () => Record<string, unknown> }));
      }
    }

    let posts = Array.from(byId.values());

    // Client-side categorie filter
    if (categoryFilter === 'uncategorized') {
      posts = posts.filter(p => !p.categoryIds || p.categoryIds.length === 0);
    } else if (categoryFilter) {
      posts = posts.filter(p => p.categoryIds?.includes(categoryFilter));
    }

    // Sorteer op datum (nieuwste eerst)
    posts.sort((a, b) => ((b.updatedAt || b.createdAt)?.getTime() ?? 0) - ((a.updatedAt || a.createdAt)?.getTime() ?? 0));

    return posts;
  }

  /** Haalt alle posts op voor een domein (alle talen), voor het tellen van categorieën. */
  async getAllPostsForDomain(domain: string): Promise<Post[]> {
    if (!domain) return [];
    const postsRef = collection(this.firestore, 'posts');
    const [snapDomain, snapDomains] = await Promise.all([
      getDocs(query(postsRef, where('domain', '==', domain))),
      getDocs(query(postsRef, where('domains', 'array-contains', domain)))
    ]);
    const byId = new Map<string, Post>();
    for (const d of [...snapDomain.docs, ...snapDomains.docs]) {
      if (!byId.has(d.id)) {
        byId.set(d.id, this.docToPost(d as unknown as { id: string; data: () => Record<string, unknown> }));
      }
    }
    return Array.from(byId.values());
  }

  async getPostBySlug(domain: string, language: string, slug: string): Promise<Post | null> {
    const postsRef = collection(this.firestore, 'posts');
    const q = query(postsRef, where('slug', '==', slug), where('language', '==', language));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    // Filter by domain
    for (const d of snap.docs) {
      const post = this.docToPost(d as unknown as { id: string; data: () => Record<string, unknown> });
      const postDomains = post.domains || (post.domain ? [post.domain] : []);
      if (postDomains.includes(domain)) return post;
    }
    return snap.docs.length > 0
      ? this.docToPost(snap.docs[0] as unknown as { id: string; data: () => Record<string, unknown> })
      : null;
  }

  async getPostById(id: string): Promise<Post | null> {
    const d = doc(this.firestore, 'posts', id);
    const snap = await getDoc(d);
    if (!snap.exists()) return null;
    return this.docToPost(snap as unknown as { id: string; data: () => Record<string, unknown> });
  }

  /**
   * Haalt alle taalversies op van een post (gekoppeld via basePostId).
   */
  async getPostsByBaseId(basePostId: string): Promise<Post[]> {
    if (!basePostId) return [];
    const postsRef = collection(this.firestore, 'posts');
    const snap = await getDocs(query(postsRef, where('basePostId', '==', basePostId)));
    const posts: Post[] = snap.docs.map((d) =>
      this.docToPost(d as unknown as { id: string; data: () => Record<string, unknown> })
    );
    // Also include the base post itself (its own id = basePostId)
    const baseDoc = await this.getPostById(basePostId);
    if (baseDoc && !posts.some((p) => p.id === baseDoc.id)) {
      posts.push(baseDoc);
    }
    return posts;
  }

  /**
   * Zoek de taalversie van een post voor een bepaalde taal.
   */
  async getPostForLanguage(basePostId: string, language: SupportedLanguage): Promise<Post | null> {
    if (!basePostId) return null;
    const baseDoc = await this.getPostById(basePostId);
    if (baseDoc?.language === language) return baseDoc;

    const postsRef = collection(this.firestore, 'posts');
    const snap = await getDocs(query(postsRef, where('basePostId', '==', basePostId), where('language', '==', language)));
    if (!snap.empty) {
      return this.docToPost(snap.docs[0] as unknown as { id: string; data: () => Record<string, unknown> });
    }
    return null;
  }

  async createPost(data: PostCreate): Promise<string> {
    const postsRef = collection(this.firestore, 'posts');
    const domainsArr = data.domains ?? [];
    const domains = domainsArr.length ? domainsArr : (data.domain ? [data.domain] : []);
    const docRef = await addDoc(postsRef, {
      title: data.title || '',
      content: data.content || '',
      language: data.language || 'nl',
      basePostId: data.basePostId || null,
      status: data.status || 'draft',
      categoryIds: data.categoryIds ?? [],
      domains,
      domain: domains[0] ?? null,
      featuredImage: data.featuredImage || null,
      slug: data.slug || '',
      metaTitle: data.metaTitle || '',
      metaDescription: data.metaDescription || '',
      metaKeywords: data.metaKeywords || [],
      ogTitle: data.ogTitle || '',
      ogDescription: data.ogDescription || '',
      ogImage: data.ogImage || null,
      htmlBlock: data.htmlBlock || '',
      createdAt: new Date(),
      updatedAt: new Date()
    });
    return docRef.id;
  }

  async updatePost(id: string, data: PostUpdate): Promise<void> {
    const d = doc(this.firestore, 'posts', id);
    const { domain: _d, ...rest } = data as PostUpdate & { domain?: string };
    const dataOut = { ...rest, updatedAt: new Date() } as Record<string, unknown>;
    const domains = (rest as { domains?: string[] }).domains;
    if (domains !== undefined) {
      dataOut['domains'] = domains.length ? domains : [];
      dataOut['domain'] = domains[0] ?? null;
    }
    await updateDoc(d, dataOut as object);
  }

  /**
   * Verwijdert een enkele taalversie van een post.
   */
  async deletePost(id: string): Promise<void> {
    await deleteDoc(doc(this.firestore, 'posts', id));
  }

  /**
   * Verwijdert alle taalversies van een post (het base-document + alle gelinkte documenten).
   */
  async deleteAllLanguageVersions(postId: string, basePostId?: string | null): Promise<void> {
    const batch = writeBatch(this.firestore);
    const effectiveBaseId = basePostId || postId;

    // Verwijder het base document
    batch.delete(doc(this.firestore, 'posts', effectiveBaseId));

    // Verwijder alle gelinkte documenten
    const postsRef = collection(this.firestore, 'posts');
    const snap = await getDocs(query(postsRef, where('basePostId', '==', effectiveBaseId)));
    snap.docs.forEach((d) => {
      batch.delete(doc(this.firestore, 'posts', d.id));
    });

    // Als postId != effectiveBaseId, verwijder ook het originele document
    if (postId !== effectiveBaseId) {
      batch.delete(doc(this.firestore, 'posts', postId));
    }

    await batch.commit();
  }
}
